import React, { useEffect, useMemo, useRef } from 'react';
import gsap from 'gsap';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Seo from './Seo';
import { careerStories } from '../data/careerStories';

const { FiArrowLeft, FiArrowRight, FiCheck, FiLinkedin } = FiIcons;

const CareerImpactPage = ({ story }) => {
  const pageRef = useRef(null);
  const storyIndex = careerStories.findIndex((item) => item.slug === story.slug);
  const previousStory = careerStories[(storyIndex - 1 + careerStories.length) % careerStories.length];
  const nextStory = careerStories[(storyIndex + 1) % careerStories.length];

  const schema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    name: story.seoTitle,
    description: story.seoDescription,
    url: `https://kwbotrous.com/career/${story.slug}/`,
    mainEntity: {
      '@type': 'Person',
      '@id': 'https://kwbotrous.com/#katherine-william-botrous',
      name: 'Katherine William-Botrous',
      sameAs: ['https://www.linkedin.com/in/kathy-william-botrous'],
      hasOccupation: {
        '@type': 'Occupation',
        name: story.role,
        occupationLocation: { '@type': 'Country', name: 'United States' },
        skills: story.focus.join(', '),
      },
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://kwbotrous.com/' },
        { '@type': 'ListItem', position: 2, name: 'Career Impact', item: 'https://kwbotrous.com/#works' },
        { '@type': 'ListItem', position: 3, name: story.company, item: `https://kwbotrous.com/career/${story.slug}/` },
      ],
    },
  }), [story]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const ctx = gsap.context(() => {
      gsap.from('.career-reveal', {
        y: 34,
        opacity: 0,
        duration: 0.8,
        stagger: 0.08,
        ease: 'power3.out',
      });
    }, pageRef);
    return () => ctx.revert();
  }, [story.slug]);

  const companyTitleClass = story.company.length > 10
    ? 'text-[clamp(2.5rem,10vw,4rem)]'
    : 'text-[clamp(3.2rem,7vw,7.5rem)]';

  return (
    <div ref={pageRef} className="min-h-screen bg-[#050505] text-white">
      <Seo
        title={story.seoTitle}
        description={story.seoDescription}
        path={`/career/${story.slug}/`}
        image={story.image}
        schema={schema}
        pageType="profile"
      />

      <header className="border-b border-white/15 px-5 md:px-10 py-6 md:py-8">
        <nav aria-label="Career story navigation" className="max-w-[1600px] mx-auto flex items-center justify-between gap-5">
          <a href="/#works" className="group inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors">
            <SafeIcon icon={FiArrowLeft} className="group-hover:-translate-x-1 transition-transform" />
            Career Impact
          </a>
          <a href="/" className="text-right font-black uppercase tracking-tight leading-[0.82] text-sm md:text-lg">
            Katherine<br />William-Botrous
          </a>
        </nav>
      </header>

      <main>
        <section className="relative min-h-[calc(100vh-87px)] grid grid-cols-1 lg:grid-cols-12 overflow-hidden">
          <div className="lg:col-span-5 px-5 md:px-10 py-16 md:py-24 flex flex-col justify-between border-b lg:border-b-0 lg:border-r border-white/15">
            <div className="career-reveal">
              <div className="text-xs font-bold tracking-[0.24em] uppercase text-[#e2c96e] mb-8">{story.eyebrow}</div>
              <div className="text-[clamp(5rem,13vw,12rem)] font-black leading-[0.7] tracking-tighter text-white/5 select-none" aria-hidden="true">{story.index}</div>
              <h1 className={`mt-[-1.2rem] ${companyTitleClass} font-black uppercase tracking-[-0.055em] leading-[0.82] whitespace-nowrap`}>{story.company}</h1>
              <p className="mt-8 max-w-xl text-xl md:text-3xl font-medium leading-tight text-white/90">{story.summary}</p>
            </div>
            <div className="career-reveal mt-16 grid grid-cols-2 gap-8 text-xs uppercase tracking-[0.16em]">
              <div>
                <div className="text-white/40 mb-2">Role</div>
                <div className="font-bold leading-relaxed">{story.role}</div>
              </div>
              <div>
                <div className="text-white/40 mb-2">Period</div>
                <div className="font-bold leading-relaxed">{story.period}</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 relative min-h-[65vh] lg:min-h-full career-reveal">
            <img src={story.image} alt={story.imageAlt} loading="eager" decoding="async" className="absolute inset-0 w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/5 to-black/20" />
            <div className="absolute bottom-7 left-7 right-7 md:bottom-10 md:left-10 md:right-10 flex flex-wrap gap-2">
              {story.focus.map((item) => (
                <span key={item} className="border border-white/40 bg-black/30 backdrop-blur-sm px-3 py-2 text-[10px] font-bold uppercase tracking-[0.14em]">{item}</span>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white text-black px-5 md:px-10 py-20 md:py-32">
          <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            <div className="lg:col-span-4">
              <div className="sticky top-10">
                <div className="text-xs font-bold tracking-[0.24em] uppercase text-[#918147] mb-5">The impact story</div>
                <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter leading-[0.85]">Context<br />before claims</h2>
                <p className="mt-8 text-base md:text-lg leading-relaxed text-[#4a4a4a]">{story.intro}</p>
              </div>
            </div>
            <div className="lg:col-span-8 border-t border-black">
              {story.chapters.map((chapter, index) => (
                <article key={chapter.title} className="grid grid-cols-[3rem_1fr] md:grid-cols-[5rem_1fr] gap-4 md:gap-8 py-10 md:py-14 border-b border-black">
                  <div className="text-xs font-bold tracking-widest text-[#918147]">/ 0{index + 1}</div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#4a4a4a] mb-4">{chapter.label}</div>
                    <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight leading-none mb-6">{chapter.title}</h3>
                    <p className="text-base md:text-xl leading-relaxed max-w-3xl">{chapter.body}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#e2c96e] text-black px-5 md:px-10 py-20 md:py-28">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-end justify-between gap-8 border-b border-black pb-8">
              <div>
                <div className="text-xs font-bold tracking-[0.24em] uppercase mb-4">Verified record</div>
                <h2 className="text-5xl md:text-8xl font-black uppercase tracking-tighter leading-[0.8]">What the<br />source supports</h2>
              </div>
              <SafeIcon icon={FiCheck} className="hidden md:block text-5xl" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-l border-black mt-12">
              {story.outcomes.map((outcome) => (
                <div data-evidence-card key={`${outcome.value}-${outcome.label}`} className="min-h-[220px] border-r border-b border-t border-black p-7 flex flex-col justify-end">
                  <div className="text-5xl md:text-6xl font-black tracking-tighter leading-none">{outcome.value}</div>
                  <div className="mt-4 text-xs font-bold uppercase tracking-[0.18em]">{outcome.label}</div>
                </div>
              ))}
            </div>

            {story.clientLogos && (
              <div className="mt-16">
                <div className="text-xs font-bold tracking-[0.24em] uppercase mb-6">Enterprise client portfolio</div>
                <div className="flex flex-wrap gap-3">
                  {story.clientLogos.map((name) => (
                    <span key={name} className="inline-block border border-black px-4 py-2 text-xs font-bold uppercase tracking-[0.12em]">
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <p className="mt-8 max-w-4xl text-sm leading-relaxed border-l-2 border-black pl-5"><strong>Source note:</strong> {story.sourceNote}</p>
          </div>
        </section>

        <section className="bg-[#050505] text-white px-5 md:px-10 py-20 md:py-24">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-16">
              <div>
                <div className="text-xs font-bold tracking-[0.2em] uppercase text-white/40">Continue exploring</div>
                <h2 className="mt-3 text-4xl md:text-6xl font-black uppercase tracking-tight">The career is one connected story.</h2>
              </div>
              <a href="https://www.linkedin.com/in/kathy-william-botrous" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#e2c96e] text-black rounded-full px-6 py-3 text-xs font-bold uppercase tracking-[0.16em]">
                LinkedIn <SafeIcon icon={FiLinkedin} />
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 border-l border-white/30">
              <a href={`/career/${previousStory.slug}/`} className="group border-r border-y border-white/30 p-7 md:p-10 hover:bg-white hover:text-black transition-colors">
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-current opacity-50">Previous chapter</div>
                <div className="mt-5 flex items-end justify-between gap-5">
                  <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight truncate">{previousStory.company}</div>
                  <SafeIcon icon={FiArrowLeft} className="text-2xl group-hover:-translate-x-1 transition-transform" />
                </div>
              </a>
              <a href={`/career/${nextStory.slug}/`} className="group border-r border-y border-white/30 p-7 md:p-10 hover:bg-white hover:text-black transition-colors">
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-current opacity-50">Next chapter</div>
                <div className="mt-5 flex items-end justify-between gap-5">
                  <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight truncate">{nextStory.company}</div>
                  <SafeIcon icon={FiArrowRight} className="text-2xl group-hover:translate-x-1 transition-transform" />
                </div>
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default CareerImpactPage;
